import type { FormSelectOption } from '@practium/ui';
import type {
	MaintenanceFormData,
	MaintenanceParcellingType,
	MaintenanceWorkspaceTab,
} from '../types';

export const CROP_STAGE_OPTIONS: FormSelectOption[] = [
	{ value: 'growth', label: 'Crescimento' },
	{ value: 'production', label: 'Produção' },
	{ value: 'prune', label: 'Poda' },
];

export const MONTH_NAMES_PT: Record<string, string> = {
	'01': 'Janeiro',
	'02': 'Fevereiro',
	'03': 'Março',
	'04': 'Abril',
	'05': 'Maio',
	'06': 'Junho',
	'07': 'Julho',
	'08': 'Agosto',
	'09': 'Setembro',
	'10': 'Outubro',
	'11': 'Novembro',
	'12': 'Dezembro',
};

export const MONTH_ABBR_PT: string[] = [
	'Jan',
	'Fev',
	'Mar',
	'Abr',
	'Mai',
	'Jun',
	'Jul',
	'Ago',
	'Set',
	'Out',
	'Nov',
	'Dez',
];

export const formatPeriodLabel = (period: string): string => {
	if (!period) return '';
	const normalized = period.replace(/\//g, '-');
	const parts = normalized.split('-');
	return MONTH_NAMES_PT[parts[1]] ?? period;
};

export const formatPeriodYear = (period: string): string => {
	if (!period) return '';
	return period.replace(/\//g, '-').split('-')[0] ?? '';
};

export const formatExecutionPeriodTitle = (period: string): string => {
	if (!period) return '';
	const normalized = period.replace(/\//g, '-');
	const [year, month] = normalized.split('-');
	const monthName = MONTH_NAMES_PT[month] ?? month;
	return `Execução de ${monthName} - ${year}`;
};

export const EXECUTION_PART_COUNT = 4;

export const EXECUTION_PART_HEADERS = ['1ª PARTE', '2ª PARTE', '3ª PARTE', '4ª PARTE'] as const;

export const PARCELLING_TYPE_OPTIONS: FormSelectOption[] = [
	{ value: 'fixed', label: 'Parcelamento Fixo' },
	{ value: 'dynamic', label: 'Parcelamento Dinâmico' },
];

export const PARCELLING_TYPE_LABELS: Record<MaintenanceParcellingType, string> = {
	fixed: 'Parcelamento fixo',
	dynamic: 'Parcelamento dinâmico',
};

export const CROP_PHASE_LABELS: Record<string, string> = {
	preFlowering: 'Pré-Florada',
	flowering: 'Florada',
	pellet: 'Chumbinho',
	expansion: 'Expansão',
	filling: 'Granação',
	maturation: 'Maturação',
	harvest: 'Colheita',
	postHarvest: 'Pós-Colheita',
};

export const MAINTENANCE_TITLES: Record<string, string> = {
	add: 'Nova Recomendação de Manutenção',
	edit: 'Editar Recomendação de Manutenção',
	view: 'Informações da Recomendação de Manutenção',
};

export const MAINTENANCE_SUBTITLES: Record<string, string> = {
	add: 'Cadastre os dados gerais da nova recomendação',
	edit: 'Edite os dados gerais da recomendação',
	view: 'Visualize os dados gerais da recomendação',
};

export const WORKSPACE_TABS: { key: MaintenanceWorkspaceTab; label: string }[] = [
	{ key: 'general', label: 'Informações Gerais' },
	{ key: 'planning', label: 'Planejamento' },
	{ key: 'execution', label: 'Execução' },
	{ key: 'nutrientDemand', label: 'Demanda Atendida' },
	{ key: 'report', label: 'Relatórios' },
];

export const APPLICATION_METHOD_OPTIONS: FormSelectOption[] = [
	{ value: 'irrigation', label: 'Fertirrigação' },
	{ value: 'broad', label: 'Lanço' },
];

export const APPLICATION_METHOD_LABELS: Record<'irrigation' | 'broad', string> = {
	irrigation: 'Fertirrigação',
	broad: 'Lanço',
};

export const FERTILIZER_PACK_SLOTS = [
	{ key: 'mix', label: 'Formulado' },
	{ key: 'N', label: 'N' },
	{ key: 'P2O5', label: 'P₂O₅' },
	{ key: 'K2O', label: 'K₂O' },
	{ key: 'Ca', label: 'Ca' },
	{ key: 'Mg', label: 'Mg' },
	{ key: 'S', label: 'S' },
	{ key: 'B', label: 'B' },
	{ key: 'Zn', label: 'Zn' },
	{ key: 'Cu', label: 'Cu' },
	{ key: 'Mn', label: 'Mn' },
	{ key: 'Fe', label: 'Fe' },
] as const;

export const PLANNING_NUTRIENT_HEADERS = [
	{ key: 'N', label: 'N' },
	{ key: 'P2O5', label: 'P₂O₅' },
	{ key: 'K2O', label: 'K₂O' },
	{ key: 'Ca', label: 'CA' },
	{ key: 'Mg', label: 'MG' },
	{ key: 'S', label: 'S' },
] as const;

export const MACRONUTRIENT_STATUS_LABELS = [
	{ key: 'N', label: 'N' },
	{ key: 'P2O5', label: 'P₂O₅' },
	{ key: 'K2O', label: 'K₂O' },
	{ key: 'Ca', label: 'Ca' },
	{ key: 'Mg', label: 'Mg' },
	{ key: 'S', label: 'S' },
] as const;

export const DEFAULT_MAINTENANCE_FORM: MaintenanceFormData = {
	name: '',
	prop: '',
	area: '',
	sectors: [],
	soilAnalysis: '',
	plantingAdvice: 'none',
	cropStage: '',
	productivity: '',
	parcellingType: '',
	parcelling: '',
	cycleStartMonth: '',
	floweringMonth: '',
};
